/**
 * アップロード済み素材（①の調理動画・レシピメモ）の見せ方と、保存先パスの組み立て。
 *
 * 保存先は `uploads/dishes/{dishId}/{kind}-{epochMs}-{安全化した元の名前}`。
 * 元の名前を残すのは人が識別するためで、接頭辞は衝突を避けるため（NewDish から
 * この形で書いてきた）。**差し替えでも既存のオブジェクトを上書きしない。**
 * 上書きすると、履歴から「当時どの素材で生成したか」を開けなくなる。
 */

export type MaterialKind = "video" | "doc";

/** パスから読み取れる範囲の素材情報。読めない形なら fileName にパス全体を入れる */
export interface Material {
  path: string;
  kind: MaterialKind | null;
  /** 表示名（接頭辞を落とした元のファイル名） */
  fileName: string;
  /** アップロード時刻（接頭辞の epoch ミリ秒）。読めなければ null */
  uploadedAt: Date | null;
}

/**
 * 品目配下の素材かどうか。
 *
 * **命名は1種類ではない。** 画面から上げたものは下の NAMED の規約に従うが、
 * 運用の初期に手で置かれた素材は時刻の区切りを持たない（実データ:
 * `video-porkliver-20260707.mp4` / `doc-④豚レバーの香り炒めレシピ.pdf`）。
 * 規約に合うものだけを素材と見なすと、**そういう品目では名前を押しても何も起きない**。
 * 置き場所（`uploads/dishes/{dishId}/` の下にあること）で判断し、
 * 名前から読めることは付加情報として扱う。
 *
 * `gs://{bucket}/` が付いた形も受ける。品目の他の欄（currentAnalysisPath）が
 * その形で入っており、素材側も同じ形で書かれている可能性があるため。
 */
const UNDER_DISH = /^(?:gs:\/\/[^/]+\/)?uploads\/dishes\/([^/]+)\/(.+)$/;

/** 画面からアップロードしたものの規約。時刻は epoch ミリ秒（13桁） */
const NAMED = /^(video|doc)-(\d{13})-(.+)$/;
/** 手で置かれた素材。種別は付いているが時刻が無い */
const KINDED = /^(video|doc)-(.+)$/;

export function parseMaterialPath(path: string): Material {
  const m = UNDER_DISH.exec(path);
  const base = (m ? m[2] : path).replace(/\/+$/, "");
  const tail = base.slice(base.lastIndexOf("/") + 1);
  if (!tail) return { path, kind: null, fileName: path, uploadedAt: null };

  const named = NAMED.exec(tail);
  if (named) {
    const ms = Number(named[2]);
    return {
      path,
      kind: named[1] as MaterialKind,
      fileName: named[3],
      uploadedAt: Number.isFinite(ms) && ms > 0 ? new Date(ms) : null,
    };
  }

  const kinded = KINDED.exec(tail);
  if (kinded) {
    // 時刻が無い＝いつ置かれたかは名前から分からない。推測して埋めない
    return { path, kind: kinded[1] as MaterialKind, fileName: kinded[2], uploadedAt: null };
  }

  return { path, kind: null, fileName: tail, uploadedAt: null };
}

/**
 * GCS のキー。`gs://{bucket}/` が付いていれば落とす。素材の置き場所でなければ null。
 *
 * **品目IDとフォルダ名の一致は要求しない。** 運用の初期に手で置かれた素材は
 * 読みやすいフォルダ名（`uploads/dishes/porkliver-20260707/`）に置かれており、
 * Firestore の品目ID と一致するとは限らない。一致を要求すると、そういう品目では
 * 素材が1つも開けなくなる（実際にそうなった）。
 *
 * **どの素材を配信してよいかは画面が決めない。** 品目が名乗っているパスかどうかは
 * 配信側（viewer/authz.mjs）が品目文書を読んで判断する。画面は指されたものを
 * そのままリンクにし、駄目なら理由が画面に出る。黙って押せないリンクを作らない。
 */
export function materialKey(path: string): string | null {
  if (!path) return null;
  const m = /^gs:\/\/[^/]+\/(.+)$/.exec(path);
  const key = m ? m[1] : path;
  return key.startsWith("uploads/") && !key.includes("..") ? key : null;
}

/**
 * 保存名の安全化。NewDish が最初に置いた規則をそのまま持ってきた。
 * 英数と `.` `-` `_`、および日本語・中国語（U+3000〜U+9FFF）だけを残す。
 */
export function safeFileName(name: string): string {
  return name.replace(/[^\w.\-　-鿿]/g, "_");
}

/** 素材の保存先。`now` を引数に取るのはテストで固定するため */
export function materialStoragePath(
  dishId: string,
  kind: MaterialKind,
  fileName: string,
  now: number,
): string {
  return `uploads/dishes/${dishId}/${kind}-${now}-${safeFileName(fileName)}`;
}

/**
 * 認可プロキシ（/m/{dishId}/{キー}）の URL。素材は署名付きURLで配らない（#13）。
 *
 * 品目IDは**認可のため**に渡す（この品目を見てよい人か、この品目の素材か）。
 * 場所はキーが持つので、フォルダ名が品目IDと違っていても届く。
 */
export function materialUrl(dishId: string, path: string): string {
  const key = materialKey(path);
  if (!dishId || key === null) return "";
  // 区切りは残して各要素だけを符号化する（日本語・中国語のファイル名がそのまま来る）
  const encoded = key.split("/").map(encodeURIComponent).join("/");
  return `/m/${encodeURIComponent(dishId)}/${encoded}`;
}

export type PreviewKind = "video" | "pdf" | "image" | "other";

const VIDEO_EXT = new Set(["mp4", "m4v", "mov", "qt", "webm", "mkv", "avi", "mpg", "mpeg", "lrf"]);
const IMAGE_EXT = new Set(["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "bmp"]);

/**
 * 画面で開ける形かどうか。
 *
 * ブラウザが再生・表示できない形（.mkv / .heic 等）も video / image に含める。
 * 「開けない」ことは実際に開いて分かる方が、種類で先回りして隠すより親切で、
 * 少なくとも別タブでの取得はできる。
 */
export function previewKind(fileNameOrPath: string): PreviewKind {
  const base = fileNameOrPath.slice(fileNameOrPath.lastIndexOf("/") + 1).toLowerCase();
  const ext = base.includes(".") ? base.slice(base.lastIndexOf(".") + 1) : "";
  if (VIDEO_EXT.has(ext)) return "video";
  if (ext === "pdf") return "pdf";
  if (IMAGE_EXT.has(ext)) return "image";
  return "other";
}

/**
 * 品目に紐付く素材。
 *
 * 分割録画は `sourceVideoPaths`（配列）が正で、`sourceVideoPath` は後方互換の単一パス
 * （worker/run-job.mjs と同じ優先順）。片方しか無い古い品目でも同じ形で読めるようにする。
 */
export function materialsOf(dish: {
  sourceVideoPaths?: string[] | null;
  sourceVideoPath?: string | null;
  recipeDocPaths?: string[] | null;
}): { videos: string[]; docs: string[] } {
  const videos =
    Array.isArray(dish.sourceVideoPaths) && dish.sourceVideoPaths.length
      ? dish.sourceVideoPaths
      : dish.sourceVideoPath
        ? [dish.sourceVideoPath]
        : [];
  return { videos: [...videos], docs: [...(dish.recipeDocPaths ?? [])] };
}
